// 📁 src/components/TradeHistoryTable.tsx
import { useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

interface Trade {
  id: string;
  symbol: string;
  pair?: string;
  side: 'BUY' | 'SELL' | string;
  price: number;
  entry_price?: number;
  exit_price?: number;
  entryPrice?: number;
  exitPrice?: number;
  qty: number;
  quantity?: number;
  amount?: number;
  size?: number;
  trade_time: string;
  timestamp?: string;
  opened_at?: string;
  closed_at?: string;
  closedAt?: string;
  time?: string;
  pnl_percent?: number;
  pnl_usd?: number;
  profit_pct?: number;
  profit_usd?: number;
  profit_amount?: number;
  profitAmount?: number;
  net_profit?: number;
  duration_minutes?: number;
  duration_seconds?: number;
  duration?: string;
  sold_pct?: number | string;
  exit_reason?: string;
  close_reason?: string;
  reason?: string;
  exit_stage?: {
    stage?: number | null;
    text: string;
    details: string;
    color: 'green' | 'red' | 'blue' | 'gray';
  } | null;
  exit_stage_details?: string;
  fee?: number;
  fees?: number;
  fee_currency?: string;
  trade_type?: 'backtest' | 'paper' | 'live';
  take_profit_stage?: number;
  take_profit_pct?: number;
  take_profit_amount?: number;
  position_size?: number;
  position_usd?: number;
}

interface TradeHistoryTableProps {
  trades: Trade[];
}

// 🔥 DINAMINIS KAINŲ APVALINIMAS: XRP matysite detaliai
const formatPrice = (p: number | undefined | null) => {
  if (p === undefined || p === null || p === 0) return '-';
  if (p < 0.1) return `$${p.toFixed(6)}`;
  if (p < 10) return `$${p.toFixed(4)}`; // Pigioms monetoms 4 skaičiai
  return `$${p.toFixed(2)}`; // BTC/ETH 2 skaičiai
};

const TradeHistoryTable = ({ trades }: TradeHistoryTableProps) => {
  const [sortField, setSortField] = useState<keyof Trade | 'duration' | 'position_usd_calc'>('trade_time');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: keyof Trade | 'duration' | 'position_usd_calc') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getDurationMs = (trade: Trade) => {
    if (trade.duration && typeof trade.duration === 'string' && trade.duration !== '-') {
       let ms = 0;
       const dMatch = trade.duration.match(/(\d+)d/);
       const hMatch = trade.duration.match(/(\d+)h/);
       const mMatch = trade.duration.match(/(\d+)m/);
       if (dMatch) ms += parseInt(dMatch[1]) * 86400000;
       if (hMatch) ms += parseInt(hMatch[1]) * 3600000;
       if (mMatch) ms += parseInt(mMatch[1]) * 60000;
       if (ms > 0) return ms;
    }
    const start = trade.opened_at;
    const end = trade.closed_at || trade.closedAt || trade.trade_time || trade.timestamp || trade.time;
    if (start && end) {
      const sTime = new Date(start).getTime();
      const eTime = new Date(end).getTime();
      if (!isNaN(sTime) && !isNaN(eTime) && eTime >= sTime) return eTime - sTime;
    }
    const seconds = trade.duration_seconds || (trade.duration_minutes ? trade.duration_minutes * 60 : 0);
    return seconds * 1000;
  };

  const formatDuration = (trade: Trade) => {
    if (trade.duration && typeof trade.duration === 'string' && trade.duration !== '-') return trade.duration;
    
    const start = trade.opened_at;
    const end = trade.closed_at || trade.closedAt || trade.trade_time || trade.timestamp || trade.time;
    if (start && end) {
      const sTime = new Date(start).getTime();
      const eTime = new Date(end).getTime();
      if (!isNaN(sTime) && !isNaN(eTime) && eTime >= sTime) {
        const diff = eTime - sTime;
        if (diff < 60000) return '< 1m';
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const parts = [];
        if (d > 0) parts.push(`${d}d`);
        if (h > 0) parts.push(`${h}h`);
        if (m > 0 || parts.length === 0) parts.push(`${m}m`);
        return parts.join(' ');
      }
    }
    const seconds = trade.duration_seconds || (trade.duration_minutes ? trade.duration_minutes * 60 : 0);
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return `${days}d ${hours}h`;
  };

  // 🔥 IŠTAISYTA PELNO FORMATAVIMO LOGIKA (Pliusų ir Minuso ženklai)
  const formatProfit = (pct: number | undefined, netUsd: number | undefined) => {
    if (pct === undefined || netUsd === undefined) return null;

    // Jei suma 0 arba daugiau, vadinasi tai pliusas, kitaip - minusas.
    const isPositive = netUsd >= 0;
    const colorClass = isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    
    const sign = isPositive ? '+' : '-';

    return (
      <div className="flex flex-col">
        <span className={`font-medium ${colorClass}`}>
          {sign}{Math.abs(pct).toFixed(2)}%
        </span>
        <span className={`text-xs ${colorClass} opacity-90`}>
          {sign}${Math.abs(netUsd).toFixed(2)}
        </span>
      </div>
    );
  };

  const formatExitStage = (trade: Trade) => {
    if (trade.exit_stage && typeof trade.exit_stage === 'object') {
      const colorClasses: Record<string, string> = {
        green: 'text-green-600 dark:text-green-400',
        red: 'text-red-600 dark:text-red-400',
        blue: 'text-blue-600 dark:text-blue-400',
        gray: 'text-gray-600 dark:text-gray-400'
      };
      const colorClass = colorClasses[trade.exit_stage.color] || colorClasses.gray;
      return (
        <div className="flex flex-col">
          <span className={`font-medium text-sm capitalize ${colorClass}`}>
            {trade.exit_stage.text}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {trade.exit_stage.details}
          </span>
        </div>
      );
    }

    const soldPercentage = trade.sold_pct !== undefined && trade.sold_pct !== null ? Number(trade.sold_pct).toFixed(2) : '100.00';
    const exitReason = String(trade.exit_reason || trade.close_reason || trade.reason || 'Strategy execution').replace(/_/g, ' ');
    const fee = trade.fee ?? trade.fees ?? 0;
    const gross = trade.profit_amount ?? trade.profitAmount ?? trade.profit ?? trade.pnl_usd ?? trade.profit_usd ?? 0;
    const netUsd = trade.net_profit !== undefined ? trade.net_profit : (gross - fee);

    let colorClass = 'text-gray-600 dark:text-gray-400';
    if (exitReason.toLowerCase().includes('take profit') || exitReason.toLowerCase().includes('take_profit') || exitReason.toLowerCase().includes('tp stage')) {
      colorClass = 'text-green-600 dark:text-green-400';
    } else if (exitReason.toLowerCase().includes('stop loss') || exitReason.toLowerCase().includes('stop_loss') || exitReason.toLowerCase().includes('trailing stop')) {
      colorClass = 'text-red-600 dark:text-red-400';
    } else if (netUsd > 0) {
      colorClass = 'text-green-600 dark:text-green-400';
    } else if (netUsd < 0) {
      colorClass = 'text-red-600 dark:text-red-400';
    }

    return (
      <div className="flex flex-col">
        <span className={`font-medium text-sm capitalize ${colorClass}`}>
          {exitReason}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Sold {soldPercentage}%
        </span>
      </div>
    );
  };

  const sortedTrades = [...trades].sort((a, b) => {
    let aValue: any; let bValue: any;
    if (sortField === 'trade_time') {
      aValue = new Date(a.closed_at || a.closedAt || a.trade_time || a.timestamp || a.time || 0).getTime();
      bValue = new Date(b.closed_at || b.closedAt || b.trade_time || b.timestamp || b.time || 0).getTime();
    } else if (sortField === 'duration') {
      aValue = getDurationMs(a); bValue = getDurationMs(b);
    } else if (sortField === 'position_usd_calc') {
      const qtyA = a.qty ?? a.quantity ?? a.amount ?? a.size ?? 0;
      const entryA = a.entry_price ?? a.entryPrice ?? a.price ?? 0;
      aValue = a.position_usd ?? a.position_size ?? (qtyA * entryA);
      const qtyB = b.qty ?? b.quantity ?? b.amount ?? b.size ?? 0;
      const entryB = b.entry_price ?? b.entryPrice ?? b.price ?? 0;
      bValue = b.position_usd ?? b.position_size ?? (qtyB * entryB);
    } else if (sortField === 'pnl_percent') {
      const feeA = a.fee ?? a.fees ?? 0;
      const grossA = a.profit_amount ?? a.profitAmount ?? a.pnl_usd ?? a.profit_usd ?? 0;
      aValue = a.net_profit ?? ((grossA || 0) - feeA);
      const feeB = b.fee ?? b.fees ?? 0;
      const grossB = b.profit_amount ?? b.profitAmount ?? b.pnl_usd ?? b.profit_usd ?? 0;
      bValue = b.net_profit ?? ((grossB || 0) - feeB);
    } else {
      aValue = a[sortField as keyof Trade]; bValue = b[sortField as keyof Trade];
    }
    if (aValue === undefined || bValue === undefined) return 0;
    if (sortDirection === 'asc') return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    else return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
  });

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  return (
    <div className="trade-history-table bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('trade_time')}>
                <div className="flex items-center space-x-1"><span>Time</span><SortIcon field="trade_time" /></div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 whitespace-nowrap" onClick={() => handleSort('duration')}>
                <div className="flex items-center space-x-1"><span>Duration</span><SortIcon field="duration" /></div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('symbol')}>
                <div className="flex items-center space-x-1"><span>Pair</span><SortIcon field="symbol" /></div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('side')}>
                <div className="flex items-center space-x-1"><span>Side</span><SortIcon field="side" /></div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('price')}>
                <div className="flex items-center justify-end space-x-1"><span>Entry</span><SortIcon field="price" /></div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Exit
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('qty')}>
                <div className="flex items-center justify-end space-x-1"><span>Quantity</span><SortIcon field="qty" /></div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('position_usd_calc')}>
                <div className="flex items-center justify-end space-x-1"><span>Position $</span><SortIcon field="position_usd_calc" /></div>
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('pnl_percent')}>
                <div className="flex items-center justify-end space-x-1"><span>Net Profit</span><SortIcon field="pnl_percent" /></div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-48">
                Exit Stage
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Fees
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Mode
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedTrades.map((trade, index) => {
              const qty = trade.quantity ?? trade.qty ?? trade.amount ?? trade.size ?? 0;
              const entry = trade.entry_price ?? trade.entryPrice ?? trade.price ?? 0;
              const exit = trade.exit_price ?? trade.exitPrice ?? 0;

              const posUsd = trade.position_usd ?? trade.position_size ?? (qty * entry);
              
              const fee = trade.fee ?? trade.fees ?? 0;
              const gross = trade.profit_amount ?? trade.profitAmount ?? trade.profit ?? trade.pnl_usd ?? trade.profit_usd ?? 0;
              const netUsd = trade.net_profit !== undefined ? trade.net_profit : (gross - fee);

              // 💥 Skaičiuojame tikslų PnL procentą iš NET USD reikšmės (kad sutaptų su pliusu/minusu)
              let netProfitPctCalculated = 0;
              if (posUsd > 0) {
                 netProfitPctCalculated = (netUsd / posUsd) * 100;
              } else if (entry > 0 && exit > 0) {
                 const isLong = trade.side?.toUpperCase() === 'BUY' || trade.side?.toUpperCase() === 'LONG';
                 netProfitPctCalculated = isLong ? ((exit - entry) / entry) * 100 : ((entry - exit) / entry) * 100;
              }

              const profitDisplay = formatProfit(netProfitPctCalculated, netUsd);
              const exitStageDisplay = formatExitStage(trade);

              const timeStr = trade.closed_at || trade.closedAt || trade.trade_time || trade.timestamp || trade.time;
              let dateDisplay = '-';
              if (timeStr) {
                try {
                   const d = new Date(timeStr);
                   dateDisplay = `${(d.getMonth()+1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}, ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
                } catch(e) {}
              }

              return (
                <tr key={trade.id || `trade-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-900/50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                    {dateDisplay}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 font-medium">
                    {formatDuration(trade)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    {trade.symbol || trade.pair}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                      trade.side?.toUpperCase() === 'BUY' || trade.side?.toUpperCase() === 'LONG'
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                        : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                    }`}>
                      {trade.side?.toUpperCase() === 'BUY' ? 'LONG' : trade.side?.toUpperCase() === 'SELL' ? 'SHORT' : trade.side}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                    {formatPrice(entry)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                    {formatPrice(exit)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                    {qty > 0 ? qty.toFixed(6) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-blue-600 dark:text-blue-400 font-bold">
                    {posUsd > 0 ? `$${posUsd.toFixed(2)}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {profitDisplay || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {exitStageDisplay}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-red-500 dark:text-red-400 font-medium">
                    {fee > 0 ? `${fee.toFixed(4)} ${trade.fee_currency || 'USDC'}` : '0.0000 USDC'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      trade.trade_type === 'live'
                        ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                        : trade.trade_type === 'paper'
                        ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
                    }`}>
                      {trade.trade_type || 'backtest'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TradeHistoryTable;